import { useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { reportsApi } from '../../api/reports'
import { Correct } from '../../components/Correct'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatTime, isAllDay } from '../../lib/calendar'
import { formatMoney } from '../../lib/format'
import { calloutsFor, kindOf, PERSONA_WORDS, ROLE_WORDS, stageWords, strengthWords, threatLabel } from '../../lib/meetingCallouts'
import type { MeetingView } from '../../types/calendar'
import { CalloutList, swatchStyle } from './MeetingChip'

// The meeting, opened in place: who is in the room and the part each plays, the account and its open
// pursuits, the tactics due, the transcript, and the prep brief on demand. Nothing navigates away.

const DAY = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })

export function MeetingDetail({ m, now, onClose, prepRequested, onPrepHandled }: { m: MeetingView; now: Date; onClose: () => void; prepRequested: boolean; onPrepHandled: () => void }) {
  const spoke = useSpoke()
  const e = m.meeting
  const kind = kindOf(m)
  const callouts = calloutsFor(m, now)
  const brief = useMutation({ mutationFn: () => reportsApi.brief(spoke, e.companyId!) })
  const outside = m.contacts.filter((c) => c.role !== 'colleague')
  const colleagues = m.contacts.filter((c) => c.role === 'colleague')
  const known = new Set(m.contacts.map((c) => c.email?.toLowerCase()).filter(Boolean))
  const unknown = e.attendeeEmails.filter((a) => !known.has(a.toLowerCase()) && a.toLowerCase() !== e.organizerEmail.toLowerCase())
  const facts = m.facts
  const threat = facts?.threat ?? null
  const transcript = facts?.transcript ?? null
  const where = e.isOnline || /^https?:\/\//i.test(e.location ?? '') ? 'Online' : (e.location ?? 'No location')
  const upcoming = new Date(e.start).getTime() >= now.getTime()

  // "Prep this" from a row or the glance lands here and runs the brief once.
  useEffect(() => {
    if (prepRequested && e.companyId && !brief.isPending && !brief.data) brief.mutate()
    if (prepRequested) onPrepHandled()
  }, [prepRequested, e.companyId]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { brief.reset() }, [e.meetingId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <aside className={`cal-detail kind-${kind}${threat ? ' threat' : ''}`} style={swatchStyle(m)} aria-label="Meeting">
      <div className="cal-detail-head">
        <div>
          <p className="kicker">{threat ? 'Competitor threat' : kind === 'client' ? 'Client meeting' : kind === 'external' ? 'Outside the firm' : kind === 'internal' ? 'Internal' : 'Just you'} · {DAY.format(new Date(e.start))}</p>
          <h2>{e.subject || '(no subject)'}</h2>
          <p className="cal-detail-when">
            {isAllDay(e.start, e.end) ? 'All day' : `${formatTime(e.start)} – ${formatTime(e.end)}`} · {where}
            {e.isCancelled && <span className="tag sig low">cancelled</span>}
          </p>
        </div>
        <button className="button small" onClick={onClose} aria-label="Close">Close</button>
      </div>

      {threat && (
        <div className="cal-threat" role="alert">
          <p className="kicker">Threat, not an opportunity</p>
          <h3>{threatLabel(threat)}</h3>
          <p>{threat.why}</p>
          {/* the meeting's own threat carries no id of its own on this payload, so the correction lands on the meeting */}
          <Correct kind="threat" entityId={e.meetingId} entityLabel={threatLabel(threat)}
            currentValue={threat.needsJudgement ? 'watch' : 'rival'} label="Not a rival?" />
          {threat.needsJudgement && <p className="muted small">Scout read the shape of a rival session here but does not know this party yet. Run the threat watch to have it judged, or add them to the register of competitors.</p>}
          <ul>
            {threat.competitorDomain && <li>Writing from <span className="mono">{threat.competitorDomain}</span></li>}
            {threat.inTheRoom.length > 0 && <li>In the room: <span className="mono">{threat.inTheRoom.slice(0, 8).join(', ')}</span>{threat.inTheRoom.length > 8 ? ` and ${threat.inTheRoom.length - 8} more` : ''}</li>}
          </ul>
        </div>
      )}

      <CalloutList callouts={callouts} onPrep={() => brief.mutate()} />

      <div className="actions">
        {e.companyId && upcoming && <button className="button primary small" onClick={() => brief.mutate()} disabled={brief.isPending} title="A meeting-prep brief from the relationship, pursuits, and signals.">{brief.isPending ? 'Preparing…' : brief.data ? 'Prep again' : 'Prep brief'}</button>}
        {e.companyId && <Link className="button small" to={`/accounts/${encodeURIComponent(e.companyId)}`}>Account plan</Link>}
        {m.transcriptId && <Link className="button small" to={`/transcripts?open=${encodeURIComponent(m.transcriptId)}`}>Transcript</Link>}
      </div>
      {brief.isError && <p className="error">{brief.error instanceof RefusedError ? brief.error.refusal.message : brief.error.message}</p>}
      {brief.data && (
        <div className="cal-brief">
          <p className="kicker">Prep brief{brief.data.modelWritten ? '' : ' (from the data; no model configured)'}</p>
          <pre className="narrative-text">{brief.data.text}</pre>
        </div>
      )}

      <h3>Who is in the room</h3>
      {outside.length === 0 && colleagues.length === 0 && unknown.length === 0 && <p className="muted small">No one but you.</p>}
      <ul className="plain cal-people">
        {outside.map((c) => (
          <li key={c.personId} className={`cal-person persona-${c.persona ?? 'none'}`}>
            <span className="cal-person-name"><i className="persona-dot" />{c.name}{c.title ? <span className="muted small"> · {c.title}</span> : null}</span>
            <span className="cal-person-meta">
              {c.persona && <span className="tag persona">{PERSONA_WORDS[c.persona] ?? c.persona}</span>}
              {c.role && c.role !== 'colleague' && <span className="tag scope">{ROLE_WORDS[c.role] ?? c.role}</span>}
              {facts?.resurfacingContactIds.includes(c.personId) && <span className="tag sig watch">gone quiet</span>}
              {facts?.movedContactIds.includes(c.personId) && <span className="tag sig watch">may have moved</span>}
              <span className={`strength-bar ${strengthWords(c.relationshipStrength)}`} title={c.relationshipStrength != null ? `Relationship strength ${Math.round(c.relationshipStrength * 100)}%` : 'Not weighed yet'}>
                <span className={`strength-fill ${strengthWords(c.relationshipStrength)}`} style={{ width: `${Math.round((c.relationshipStrength ?? 0) * 100)}%` }} />
              </span>
            </span>
          </li>
        ))}
        {colleagues.map((c) => (
          <li key={c.personId} className="cal-person colleague"><span className="cal-person-name">{c.name}</span><span className="cal-person-meta"><span className="tag scope">Colleague</span></span></li>
        ))}
        {unknown.map((a) => (
          <li key={a} className="cal-person unknown"><span className="cal-person-name mono small">{a}</span><span className="cal-person-meta"><span className="muted small">not in your contacts</span></span></li>
        ))}
      </ul>

      {(m.companyName || (facts && facts.openPursuits.length > 0)) && (
        <>
          <h3>The account</h3>
          {m.companyName && e.companyId && (
            <p className="cal-account">
              <Link to={`/accounts/${encodeURIComponent(e.companyId)}`}>{m.companyName}</Link>
              {facts?.firstWithAccount && <span className="tag stage lead">first meeting</span>}
              <Correct kind="meeting-account" entityId={e.meetingId} entityLabel={e.subject || m.companyName} currentValue={`company:${e.companyId}`} compact />
            </p>
          )}
          {facts && facts.openPursuits.length > 0 ? (
            <ul className="plain cal-pursuits">
              {facts.openPursuits.map((p) => (
                <li key={p.engagementId}>
                  <span className={`tag stage ${p.stage}`}>{stageWords(p.stage)}</span>
                  <span className="cal-pursuit-subject">{p.subject}</span>
                  {p.amount != null && <span className="cal-pursuit-amount">{formatMoney(p.amount)}</span>}
                  {p.nextStep && <span className="muted small cal-pursuit-next">Next: {p.nextStep}{p.nextStepBy ? ` by ${p.nextStepBy}` : ''}</span>}
                </li>
              ))}
            </ul>
          ) : <p className="muted small">No open pursuit at this account.</p>}
        </>
      )}

      {facts && facts.tacticsDue.length > 0 && (
        <>
          <h3>Tactics due</h3>
          <ul className="plain cal-tactics">
            {facts.tacticsDue.map((t) => (
              <li key={t.applicationId}>
                <Link to="/playbook">{t.tacticName}</Link>
                <span className="muted small">planned {t.appliedOn}</span>
                <Correct kind="play" entityId={t.applicationId} entityLabel={t.tacticName} currentValue={`tactic:${t.tacticId}`} compact />
              </li>
            ))}
          </ul>
        </>
      )}

      <h3>After the meeting</h3>
      {transcript ? (
        <p className="cal-transcript">
          <Link to={`/transcripts?open=${encodeURIComponent(transcript.transcriptId)}`}>{transcript.meeting || transcript.clipName}</Link>
          <span className="muted small">{transcript.wordCount.toLocaleString()} words{transcript.speakers.length ? ` · ${transcript.speakers.join(', ')}` : ''}{transcript.hasRecording ? ' · recording on file' : ''}</span>
        </p>
      ) : m.transcriptId ? (
        <p className="cal-transcript"><Link to={`/transcripts?open=${encodeURIComponent(m.transcriptId)}`}>Transcript</Link></p>
      ) : (
        <p className="muted small">{upcoming ? 'A transcript ties itself here after the meeting, once the scribe library has it.' : 'No transcript linked. Import the scribe ledger, then sync, to tie one here.'}</p>
      )}
    </aside>
  )
}
